import Papa from "papaparse";
import _ from "lodash";

import {
  getSessionOptions,
  getFlightOptions,
  getStateOptions,
  getGenderOptions,
  getRawOrEquippedOptions,
  getScoreByOptions,
  getDivisionCodeOptions,
  getCountryOptions,
} from "util/options";
import { parseDateString, formatDate } from "util/dateHelper";

const findOptionValue = function (options, value) {
  return _.get(
    _.find(options, (i) => {
      return (
        _.toLower(i.label) === _.toLower(value) ||
        _.toLower(i.value) === _.toLower(value)
      );
    }),
    "value"
  );
};

const findSubDocumentByName = function (documents, value) {
  return _.find(
    documents,
    (d) => _.trim(_.toLower(d.name)) === _.trim(_.toLower(value))
  );
};

const getIsEmptyRow = function (row) {
  return _.every(row, (value) => _.isNil(value) || value === "");
};

const cleanInput = function (row) {
  return _.mapValues(row, (value) => {
    value = _.replace(value, "’", "'");
    value = _.replace(value, "”", '"');
    value = _.replace(value, "‘", "'");
    value = _.replace(value, "“", '"');
    value = _.trim(value);
    return value;
  });
};

const cleanNumber = function (num) {
  if (_.isNaN(_.toNumber(num)) || _.isUndefined(num) || num === "") {
    return undefined;
  }

  return _.toNumber(num);
};

const dataMatch = function (submittedValue, convertedValue) {
  return !!submittedValue === !!convertedValue;
};

export const parseLifters = function (csvString, meet) {
  const liftersData = Papa.parse(csvString, { header: true });
  const newLifters = [];
  let previousLifter;
  _.each(liftersData.data, (lifter, index) => {
    const isEmptyRow = getIsEmptyRow(lifter);
    if (!isEmptyRow) {
      lifter = cleanInput(lifter);
      const newLifter = {
        _id: index,
        name: lifter.name,
        team: lifter.team,
        lot: cleanNumber(lifter.lot),
        platformId: _.get(
          findSubDocumentByName(meet.platforms, lifter.platform),
          "_id"
        ),
        session: findOptionValue(getSessionOptions(), lifter.session),
        flight: findOptionValue(getFlightOptions(), lifter.flight),
        birthDate:
          lifter.birthDate &&
          formatDate({
            date: parseDateString({ dateString: lifter.birthDate, meet }),
            meet,
          }),
        memberNumber: lifter.memberNumber,
        state: findOptionValue(
          getStateOptions(null, meet),
          lifter.state || lifter.province
        ),
        country: findOptionValue(getCountryOptions(null, meet), lifter.country),
        gender: findOptionValue(getGenderOptions(lifter, meet), lifter.gender),
        divisions: [],
        bodyWeight: cleanNumber(lifter.bodyWeight),
        squatRackHeight: lifter.squatRackHeight,
        benchRackHeight: lifter.benchRackHeight || lifter.pressRackHeight,
        lifts: {
          squat: {
            1: {
              weight: cleanNumber(lifter.squat1),
            },
          },
          bench: {
            1: {
              weight: cleanNumber(lifter.bench1 || lifter.press1),
            },
          },
          dead: {
            1: {
              weight: cleanNumber(lifter.dead1),
            },
          },
        },
        email: lifter.email,
      };
      console.log("newLifter", newLifter);

      // TODO: handle invalid lot numbers, body weight and birthdates
      newLifter.match = {
        platformId: dataMatch(lifter.platform, newLifter.platformId),
        session: dataMatch(lifter.session, newLifter.session),
        flight: dataMatch(lifter.flight, newLifter.flight),
        state: dataMatch(lifter.state || lifter.province, newLifter.state),
        country: dataMatch(lifter.country, newLifter.country),
        gender: dataMatch(lifter.gender, newLifter.gender),
      };

      // consider rows with empty names to be extra divisions for previous row.
      const divisionRow = _.isEmpty(lifter.name) && previousLifter;
      const currentLifter = divisionRow ? previousLifter : newLifter;

      const divisionDocument = findSubDocumentByName(
        meet.divisions,
        lifter.division
      );
      const rawOrEquipped = findOptionValue(
        getRawOrEquippedOptions(null, meet),
        lifter.rawOrEquipped
      );
      const divisionId =
        _.get(divisionDocument, "rawOrEquipped") === rawOrEquipped &&
        _.get(divisionDocument, "gender") === currentLifter.gender
          ? _.get(divisionDocument, "_id")
          : null;
      const newDivision = {
        rawOrEquipped,
        divisionId,
        declaredAwardsWeightClassId: _.get(
          findSubDocumentByName(
            _.get(divisionDocument, "weightClasses"),
            lifter.declaredAwardsWeightClass
          ),
          "_id"
        ),
      };

      newDivision.match = {
        rawOrEquipped: dataMatch(lifter.rawOrEquipped, rawOrEquipped),
        divisionId: dataMatch(lifter.division, divisionId),
        declaredAwardsWeightClassId: dataMatch(
          lifter.declaredAwardsWeightClass,
          newDivision.declaredAwardsWeightClassId
        ),
      };

      if (divisionRow) {
        previousLifter.divisions.push(newDivision);
      } else {
        newLifter.divisions.push(newDivision);
        newLifters.push(newLifter);
        previousLifter = newLifter;
      }
    }
  });

  return newLifters;
};

export const parseDivisions = function (csvString, meet) {
  const divisionsData = Papa.parse(csvString, { header: true });
  const newDivisions = [];
  let previousDivision;
  _.each(divisionsData.data, (division, index) => {
    const isEmptyRow = getIsEmptyRow(division);
    if (!isEmptyRow) {
      division = cleanInput(division);
      const lifts = { squat: false, bench: false, dead: false };
      const importedLifts = _.split(_.trim(division.lifts, " "), ",");
      _.each(importedLifts, (l) => {
        if (_.includes(_.toLower(l), "squat")) {
          lifts.squat = true;
        }
        if (_.includes(_.toLower(l), "bench")) {
          lifts.bench = true;
        }
        if (_.includes(_.toLower(l), "dead")) {
          lifts.dead = true;
        }
      });
      const newDivision = {
        _id: index,
        name: division.name,
        gender: findOptionValue(getGenderOptions(null, meet), division.gender),
        rawOrEquipped: findOptionValue(
          getRawOrEquippedOptions(null, meet),
          division.rawOrEquipped
        ),
        lifts,
        scoreBy: findOptionValue(getScoreByOptions(), division.scoreBy),
        weightClasses: {},
        usaplDivisionCode: findOptionValue(
          getDivisionCodeOptions(null, meet),
          division.usaplDivisionCode
        ),
        hideOnBoard: division.hideOnBoard === "true",
      };

      // TODO: handle lifts matching
      newDivision.match = {
        gender: dataMatch(division.gender, newDivision.gender),
        rawOrEquipped: dataMatch(
          division.rawOrEquipped,
          newDivision.rawOrEquipped
        ),
        scoreBy: dataMatch(division.scoreBy, newDivision.scoreBy),
        usaplDivisionCode: dataMatch(
          division.usaplDivisionCode,
          newDivision.usaplDivisionCode
        ),
      };

      const weightClassId = `w-${index}`;
      const newWeightClass = {
        _id: weightClassId,
        name: division.weightClassName,
        maxWeight: cleanNumber(division.maxWeight),
      };

      // consider rows with empty names to be extra weight classes for previous row.
      if (_.isEmpty(division.name)) {
        if (previousDivision) {
          previousDivision.weightClasses[weightClassId] = newWeightClass;
        }
      } else {
        newDivision.weightClasses[weightClassId] = newWeightClass;
        newDivisions.push(newDivision);
        previousDivision = newDivision;
      }
    }
  });

  return newDivisions;
};
